import { P, match } from '@frontend/duck-tape';
import { createDraftTaskRouteValidator } from '@frontend/member-route-validators';
import { CreateDraftTaskProvider, useDraftCreationFlow } from '@frontend/react-domain';
import { PageContainer, Spinner } from '@frontend/web-react';
import { Checkout } from '@frontend/web/components';
import { Description } from '@frontend/web/components/DomainSpecific/DraftTasks/Description';
import { DraftCreationHeader } from '@frontend/web/components/DomainSpecific/DraftTasks/DraftCreationHeader';
import { InformationGathering } from '@frontend/web/components/DomainSpecific/DraftTasks/InformationGathering/InformationGathering';
import { useAccessControl, useAnalytics, useLazyRetrieveDraftTaskQuery } from '@frontend/web/hooks';
import { disconnectSocket, getSocket, initializeSocket } from '@frontend/web/utils/sockets';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';

const CreateTaskContent = () => {
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const { track } = useAnalytics();

  const params = {
    description: search?.description,
    example: search?.example,
    letterRecommendationId: search?.letterRecommendationId,
    letterRecommendationTaskSuggestionIdx: search?.letterRecommendationTaskSuggestionIdx,
    name: search?.name,
    suggestionId: search?.suggestionId,
  };

  const { currentParentStep, decrementParentStep, incrementParentStep, setupSocket, startOver } = useDraftCreationFlow({
    initialDraftTaskId: undefined,
    onDraftAutoTransitioned: (taskId) => {
      if (taskId) {
        track({ data: { entryPoint: 'new_task_page' }, event: 'draftTaskCheckoutCompleted', topic: 'draftTasks' });
        navigate({ params: { taskId }, replace: true, to: '/app/task/$taskId' });
        return;
      }
      navigate({ to: '/app/home' });
    },
    params,
    socketClient: {
      disconnectSocket,
      getSocket,
      initializeSocket,
    },
  });

  const renderContent = () => {
    return match(currentParentStep)
      .with('description', () => (
        <Description
          description={params.description}
          incrementParentStep={incrementParentStep}
          isFromSignup={search?.isFromSignUp}
          setupSocket={setupSocket}
        />
      ))
      .with('information_gathering', () => (
        <InformationGathering decrementParentStep={decrementParentStep} incrementParentStep={incrementParentStep} />
      ))
      .with('checkout', () => (
        <Checkout
          decrementParentStep={decrementParentStep}
          entryPoint="new_task_page"
          incrementParentStep={incrementParentStep}
          startOver={startOver}
        />
      ))
      .with(P.nullish, () => <Spinner color="neutral.9" />)
      .exhaustive();
  };

  return (
    <PageContainer
      HeaderComponent={<DraftCreationHeader currentStep={currentParentStep} />}
      centered
      className="bg-trueWhite"
      isScrollable={false}
      padding="none"
    >
      {renderContent()}
    </PageContainer>
  );
};

const CreateDraftTask = () => {
  const { checkAccessAndRedirect, isClientSubscribed } = useAccessControl();

  useEffect(() => {
    checkAccessAndRedirect();
  }, [isClientSubscribed]);

  if (!isClientSubscribed) {
    return null;
  }

  return (
    <CreateDraftTaskProvider useLazyRetrieveDraftTaskQuery={useLazyRetrieveDraftTaskQuery}>
      <CreateTaskContent />
    </CreateDraftTaskProvider>
  );
};

export const Route = createFileRoute('/app/draft-task/create')({
  component: CreateDraftTask,
  validateSearch: createDraftTaskRouteValidator,
});
